import React from 'react';
import { NewModal } from 'lib/components';
import { Flex } from 'lib/components/styles/layout';
import { Button, Typography } from 'react-covideo-common';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import { FileDropZone } from 'lib/components/inputs/FileDropZone/FileDropZone';
import { CreatePronunciationValues } from '../Pronunciations';
import { ModalHeader } from 'app/pages/guides/modals/VisibilityModal';
import { MdClose } from 'react-icons/md';
import { ParagraphExtraSmall } from 'lib/components/styles/typography';
import { IoArrowForward } from 'react-icons/io5';
import { VoiceRecorder } from 'app/pages/library/components/audio/VoiceRecorder';
import { VoiceRecorderWrapper } from './styles';
import { generateAudioFileName } from 'app/pages/library/components/audio/helpers';
import { useAuth } from 'lib/context';
import { useTheme } from 'styled-components/macro';

interface CreatePronunciationModalProps {
  onClose: () => void;
  onSave: (data: CreatePronunciationValues) => void;
  isLoading: boolean;
}

const PronunciationSchema = Yup.object<CreatePronunciationValues>()
  .shape({
    title: Yup.string()
      .min(2, 'Title is too short')
      .max(255, 'Title is too long')
      .required('Title is required'),

    droppedAudioFile: Yup.mixed<File>().nullable(),
    recordedAudioFile: Yup.mixed<File>().nullable(),
  })
  .test(
    'at-least-one-file',
    'Please upload or record an audio file.',
    function (
      this: Yup.TestContext,
      values: CreatePronunciationValues | undefined
    ): boolean | Yup.ValidationError {
      if (!values?.droppedAudioFile && !values?.recordedAudioFile) {
        return this.createError({
          path: 'audioFile',
          message: 'Please upload or record an audio file.',
        });
      }
      return true;
    }
  );

export const CreatePronunciationModal = ({
  onClose,
  onSave,
  isLoading,
}: CreatePronunciationModalProps) => {
  const { userData } = useAuth();
  const theme = useTheme();
  const initialValues = {
    title: '',
    droppedAudioFile: null as File | null,
    recordedAudioFile: null as File | null,
  };
  const FILE_NAME_PREFIX = 'pronunciation';
  const FILE_TYPE = 'webm';
  const addPronunciationText = 'Add Pronunciation';

  const isValidRecording = (isValid: boolean) => {
    return isValid;
  };

  const handleRecordingGeneration = async (blobUrl: string) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    const audioFileName = generateAudioFileName(
      FILE_NAME_PREFIX,
      FILE_TYPE,
      userData
    );
    const file = new File([blob], audioFileName, { type: blob.type });
    return file;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={PronunciationSchema}
      onSubmit={values => onSave(values)}
    >
      {({
        handleSubmit,
        setFieldValue,
        isSubmitting,
        values,
        errors,
        isValid,
      }) => (
        <NewModal
          hideHeader
          closeModal={isLoading ? () => {} : onClose}
          style={{ content: { width: '100vw', height: '100vh', padding: 0 } }}
        >
          <ModalHeader>
            <Button
              variant='transparent'
              icon={<MdClose size='18px' />}
              onClick={onClose}
              disabled={isSubmitting}
            />
            <Typography
              fontWeight={600}
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                whiteSpace: 'nowrap',
              }}
            >
              {addPronunciationText}
            </Typography>
            <Button
              variant='primary'
              text={addPronunciationText}
              icon={<IoArrowForward />}
              iconPosition='right'
              onClick={() => handleSubmit()}
              disabled={isSubmitting || !isValid}
            />
          </ModalHeader>
          <Flex
            flexDirection='column'
            justifyContent='center'
            alignItems='center'
            width='100%'
            padding='21px 0px'
          >
            <Flex
              flexDirection='column'
              width='100%'
              maxWidth='737px'
              alignItems='stretch'
              gap='40px'
            >
              <Form>
                <Flex flexDirection='column' gap='40px' width='100%'>
                  <Flex gap='19px'>
                    <Flex gap='15px'>
                      <Typography
                        variant='small'
                        fontWeight={600}
                        style={{ alignSelf: 'start' }}
                      >
                        Pronunciation Details
                      </Typography>
                      <FormikInputField
                        name='title'
                        label='Pronunciation Title:'
                        isRequired={false}
                      />
                    </Flex>
                    <Flex
                      alignItems='center'
                      justifyContent='space-between'
                      gap='34px'
                      width='100%'
                    >
                      <Flex gap='4px'>
                        <FileDropZone
                          accept={['audio/*']}
                          onFileLoaded={(_, __, file) => {
                            setFieldValue('droppedAudioFile', file);
                          }}
                          onFileRemoved={() => {
                            setFieldValue('droppedAudioFile', null);
                          }}
                          extendStyles={{
                            container: { height: '320px', width: '100%' },
                          }}
                          disabled={isSubmitting || !!values.recordedAudioFile}
                        />
                        {/* Remove this once FileDropZone becomes Formik compatible component */}
                        <ParagraphExtraSmall color={theme.colors.danger[80]}>
                          {(errors as any).audioFile || '\u00A0'}
                        </ParagraphExtraSmall>
                      </Flex>

                      <Flex gap='8px' alignItems='center'>
                        <Typography variant='normal'>Or</Typography>
                        <Typography variant='normal' fontWeight={600}>
                          Record Pronunciation
                        </Typography>
                        <Typography
                          variant='extra-small'
                          color={theme.colors.neutral[80]}
                        >
                          For best results, record yourself saying the word
                          slowly and clearly twice in a quiet space with no
                          background noise.
                        </Typography>
                      </Flex>
                    </Flex>
                  </Flex>
                  <VoiceRecorderWrapper>
                    <VoiceRecorder
                      onChangeRecordingValid={isValidRecording}
                      onRecordingUrlGeneration={async blobUrl => {
                        const file = await handleRecordingGeneration(blobUrl);
                        if (file) {
                          setFieldValue('recordedAudioFile', file);
                        }
                      }}
                      onRetakeRecording={() => {
                        setFieldValue('recordedAudioFile', null);
                      }}
                      disabled={isSubmitting || !!values.droppedAudioFile}
                      extendedStyles={{ wrapper: { width: '100%' } }}
                    />
                  </VoiceRecorderWrapper>
                </Flex>
              </Form>
            </Flex>
          </Flex>
        </NewModal>
      )}
    </Formik>
  );
};
